import {
  NotificationDigestCadence,
  NotificationPreferenceScope,
  SetNotificationPreferenceInput,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";

/**
 * Draft state for the notification preferences page: every toggle position the
 * reader is currently looking at, and the diff against what the server said.
 *
 * All of it is pure. The page is a large grid of switches whose meaning depends
 * on three interacting layers (a per-notification choice, a channel parent and a
 * section parent), and the rules that matter most — "only send what was touched",
 * "a parent never moves the switches beneath it", "a muted section still shows its
 * stored positions" — are exactly the rules that are invisible in a rendered
 * screenshot. Keeping them in functions that take state and return state means they
 * can be asserted directly instead of through the DOM.
 *
 * The one hook at the bottom is deliberately thin: it holds a `useState` and
 * re-seeds it when the server answer changes. Everything it does with the state
 * is one of the functions above it.
 */

/** Delivery channels a preference row can carry a toggle for. */
export const NOTIFICATION_CHANNEL_IN_APP = "IN_APP";
export const NOTIFICATION_CHANNEL_EMAIL = "EMAIL";

/**
 * The channels the page draws a column for, in column order. One list, shared by
 * the rendering and by the delivery arithmetic: a page that counted a channel it
 * never drew, or drew one it never counted, would contradict its own header.
 */
export const PREFERENCE_ROW_CHANNELS = [
  NOTIFICATION_CHANNEL_IN_APP,
  NOTIFICATION_CHANNEL_EMAIL,
] as const;

/**
 * The fields this module reads off a catalog parent switch. Narrower than the
 * generated type on purpose: the logic depends on the switch's coordinates and
 * its position, so a test can state a case in four lines rather than
 * reconstructing a whole GraphQL payload.
 *
 * `kind` and `channel` are carried straight through to the save payload and are
 * never rebuilt — the sentinel format is storage detail the server owns.
 */
export type DraftParentSwitch = {
  kind: string;
  channel: string;
  enabled: boolean;
};

/** The fields this module reads off one channel's entry in a row. */
export type DraftRowPreference = {
  channel: string;
  /**
   * The stored per-notification choice with parents ignored: the toggle position.
   * Never `enabled`, which is the folded delivered value and would render every
   * switch off under a mute — and then save those falses over the stored choices.
   */
  ownEnabled: boolean;
  /** What the kind ships with, which is what the Standard preset restores. */
  defaultEnabled: boolean;
};

export type DraftCatalogRow = {
  kind: string;
  /** Only the channels this kind supports. An absent channel gets no toggle. */
  preferences: readonly DraftRowPreference[];
};

export type DraftCatalogSection = {
  groupId: string;
  /** One per channel, matching the columns the section's switches sit in. */
  parentSwitches: readonly DraftParentSwitch[];
  rows: readonly DraftCatalogRow[];
};

/** The fields this module reads off the catalog's digest schedule. */
export type DraftEmailDigest = {
  cadence: NotificationDigestCadence;
  /** The zone the server resolved. Displayed nowhere; kept so a save can diff it. */
  timeZone: string;
};

export type DraftCatalog = {
  channelParentSwitches: readonly DraftParentSwitch[];
  emailDigest: DraftEmailDigest;
  sections: readonly DraftCatalogSection[];
};

/**
 * What the delivery arithmetic below needs from a row: which channels exist at
 * all. The stored positions come from the draft rather than from the catalog,
 * which is the whole point — the count has to move as the reader flips switches,
 * not describe the answer the server gave.
 *
 * Split out from {@link DraftCatalogRow} so a rendering component, which carries
 * presentation fields the catalog type knows nothing about, can pass itself
 * straight in.
 */
export type DeliveryRow = {
  kind: string;
  preferences: readonly { channel: string }[];
};

export type DeliverySection = {
  parentSwitches: readonly DraftParentSwitch[];
  rows: readonly DeliveryRow[];
};

/** One toggle in the draft: where it lives, and where the reader has put it. */
export type DraftToggle = {
  kind: string;
  channel: string;
  enabled: boolean;
};

/**
 * Every toggle on the page, keyed by its coordinates.
 *
 * A record rather than a Map so React sees a new object per edit without any
 * cloning ceremony, and so the key order is the order the catalog gave —
 * which is what makes the save payload deterministic.
 */
export type NotificationPreferenceDraft = Record<string, DraftToggle>;

/**
 * Coordinates of one toggle. Parents and rows share the keyspace because they
 * share it in storage too: a parent is a preference row with a sentinel kind, so
 * giving them separate keyspaces here would invent a distinction the platform
 * does not have.
 */
export function draftKey(kind: string, channel: string): string {
  return `${kind}::${channel}`;
}

/**
 * The draft as it stands the instant the server answer arrives — the snapshot
 * every "is this dirty" question is asked against.
 *
 * Parents are seeded from `enabled` because a parent's resolved position *is* its
 * own position; nothing sits above it. Rows are seeded from `ownEnabled` because
 * something does — seeding a row from the folded `enabled` would draw every switch
 * in a muted section as off and then save those falses over the choices the
 * sentinel rows exist to preserve.
 */
export function buildDraftFromCatalog(
  catalog: DraftCatalog | null | undefined
): NotificationPreferenceDraft {
  const draft: NotificationPreferenceDraft = {};
  if (!catalog) {
    return draft;
  }

  for (const section of catalog.sections) {
    for (const row of section.rows) {
      for (const preference of row.preferences) {
        draft[draftKey(row.kind, preference.channel)] = {
          kind: row.kind,
          channel: preference.channel,
          enabled: preference.ownEnabled,
        };
      }
    }
  }

  // Every parent switch, seeded from the position the server resolved for it. Not
  // derived from the rows underneath: a parent that is off while its rows are on is
  // the state this whole page exists to express, and deriving would erase it on
  // first paint.
  const parents = [
    ...catalog.channelParentSwitches,
    ...catalog.sections.flatMap((section) => section.parentSwitches),
  ];
  for (const parent of parents) {
    draft[draftKey(parent.kind, parent.channel)] = {
      kind: parent.kind,
      channel: parent.channel,
      enabled: parent.enabled,
    };
  }

  return draft;
}

/**
 * The draft with one toggle moved. Every other entry is carried through
 * untouched, which is the whole reason a parent can be flipped without
 * disturbing the rows beneath it: flipping a parent writes one key, and the
 * dimming the reader then sees is computed rather than stored.
 */
export function setDraftToggle(
  draft: NotificationPreferenceDraft,
  kind: string,
  channel: string,
  enabled: boolean
): NotificationPreferenceDraft {
  return { ...draft, [draftKey(kind, channel)]: { kind, channel, enabled } };
}

/**
 * How many of a group are on, for the copy beside a parent switch.
 *
 * Counts the rows' own stored positions, with the parents ignored. That is what
 * keeps the number meaningful under a mute: the promise a parent makes is that the
 * choices beneath it are still there, so a count that collapsed to zero the moment
 * a parent went off would contradict the one thing the reader needs to trust.
 */
export function channelAggregate(
  draft: NotificationPreferenceDraft,
  kinds: readonly string[],
  channel: string
): { total: number; on: number; allOn: boolean } {
  let total = 0;
  let on = 0;
  for (const kind of kinds) {
    const toggle = draft[draftKey(kind, channel)];
    if (!toggle) {
      continue;
    }
    total += 1;
    if (toggle.enabled) {
      on += 1;
    }
  }
  return { total, on, allOn: total > 0 && on === total };
}

/** The current position of one toggle, or false for a toggle the page has none of. */
export function draftValue(
  draft: NotificationPreferenceDraft,
  kind: string,
  channel: string
): boolean {
  return draft[draftKey(kind, channel)]?.enabled ?? false;
}

/**
 * Only the toggles whose position differs from the server's, in catalog order.
 *
 * This is what the save sends, and the "only" is load-bearing. Sending every
 * toggle would write a row for every notification on the page, turning values that
 * were inheriting from the organization (or from the kind's own policy) into frozen
 * personal overrides — so a later change to an organization default would stop
 * reaching anyone who had ever visited this page.
 *
 * Parent switches take part on exactly the same terms. A moved parent is one row to
 * write; the rows beneath it are untouched and therefore absent, which is precisely
 * how a mute silences without clearing.
 */
export function dirtyToggles(
  draft: NotificationPreferenceDraft,
  snapshot: NotificationPreferenceDraft
): DraftToggle[] {
  return Object.keys(draft).flatMap((key) => {
    const current = draft[key];
    const original = snapshot[key];
    if (!current || !original || current.enabled === original.enabled) {
      return [];
    }
    return [current];
  });
}

/**
 * A cadence edit, resolved to the row it is stored on.
 *
 * Cadence lives on the email channel master — the same row that says whether
 * email arrives at all — so it carries that switch's coordinates and its current
 * position. Both edits therefore travel as **one** entry: sending two entries for
 * the same row would have the second silently decide `enabled` for the first.
 */
export type DraftCadenceChange = {
  kind: string;
  channel: string;
  /** The master's position in the draft right now, moved or not. */
  enabled: boolean;
  cadence: NotificationDigestCadence;
  /**
   * The reader's own IANA zone, so their digest lands in their morning — and absent on
   * the organization tab, where the browser's zone is the admin's rather than anyone
   * else's. A zone is personal; a cadence is a policy an organization may set.
   */
  timeZone?: string;
};

/**
 * The mutation input for a save.
 *
 * `leadTimeDays` and `thresholdPercent` are deliberately omitted rather than
 * sent: the "When" column is read-only for now, so the page has no per-reader
 * timing to store, and sending the value it is *displaying* would convert a
 * number that was inherited into an override nobody chose.
 *
 * Omitting them is safe because the API treats an absent field as "leave the
 * stored value alone" and only a field sent as null as "clear it". A save that
 * moves one switch therefore cannot disturb a threshold or lead time already
 * stored on that row.
 *
 * The same rule is what lets the cadence entry replace the email master's toggle
 * entry rather than sit beside it: it carries `enabled` too, and the fields it
 * does not name are left alone.
 */
export function buildSavePayload(
  toggles: readonly DraftToggle[],
  scope: NotificationPreferenceScope,
  cadenceChange?: DraftCadenceChange | null
): SetNotificationPreferenceInput[] {
  const entries: SetNotificationPreferenceInput[] = toggles
    .filter(
      (toggle) =>
        !cadenceChange ||
        toggle.kind !== cadenceChange.kind ||
        toggle.channel !== cadenceChange.channel
    )
    .map((toggle) => ({
      scope,
      kind: toggle.kind,
      channel: toggle.channel,
      enabled: toggle.enabled,
    }));

  if (cadenceChange) {
    entries.push({
      scope,
      kind: cadenceChange.kind,
      channel: cadenceChange.channel,
      enabled: cadenceChange.enabled,
      digestCadence: cadenceChange.cadence,
      digestTimeZone: cadenceChange.timeZone,
    });
  }
  return entries;
}

/**
 * The reader's own zone, as the browser reports it.
 *
 * Read at save time rather than stored, and sent only alongside a cadence the
 * reader actually chose. Sending it on load would rewrite the stored zone of
 * anyone who opened the page from a hotel — and would make the page dirty on
 * arrival, which is the one thing a settings page must never be.
 */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Whether one channel of one row would actually be delivered: its own position
 * ANDed with the two parents above it.
 *
 * The same fold the server applies at fan-out, computed from the draft so the page
 * agrees with itself before anything is saved. It is deliberately *not* what a
 * switch renders — a switch shows its own stored position — but it is what the
 * dimming, the off badge and the header count are derived from.
 *
 * A channel the row has no entry for cannot deliver at all, which is why that is
 * checked first rather than defaulted to on.
 */
export function isChannelDelivering({
  row,
  channel,
  draft,
  channelParentSwitches,
  sectionParentSwitches,
}: {
  row: DeliveryRow;
  channel: string;
  draft: NotificationPreferenceDraft;
  channelParentSwitches: readonly DraftParentSwitch[];
  sectionParentSwitches: readonly DraftParentSwitch[];
}): boolean {
  const supported = row.preferences.some((preference) => preference.channel === channel);
  if (!supported) {
    return false;
  }
  if (!isParentChannelOn(draft, channelParentSwitches, channel)) {
    return false;
  }
  if (!isParentChannelOn(draft, sectionParentSwitches, channel)) {
    return false;
  }
  return draftValue(draft, row.kind, channel);
}

/**
 * Whether the parent governing one channel is on, reading the draft so an unsaved
 * flip takes effect immediately.
 *
 * A channel with no parent switch at all is on: absence has to mean "nothing is
 * silencing this", the same rule the server applies to a missing row. Defaulting to
 * off would grey a whole column because a payload was one field short.
 */
export function isParentChannelOn(
  draft: NotificationPreferenceDraft,
  // Only the coordinates, never the position: the answer comes from the draft, so
  // a caller holding a switch without its server-side `enabled` — the row header,
  // which renders positions from the draft too — can still ask.
  parents: readonly { kind: string; channel: string }[],
  channel: string
): boolean {
  const parent = parents.find((entry) => entry.channel === channel);
  if (!parent) {
    return true;
  }
  return draftValue(draft, parent.kind, parent.channel);
}

/**
 * How many of a section's rows would actually fire — the count a section header
 * shows.
 *
 * Delivered rather than stored, so a parent mute takes it to zero: the number
 * answers "what will reach me", which is the question a reader is asking when they
 * glance at a section header. The stored choices are not lost and the switches below
 * still show them; the count simply stops claiming they are arriving.
 */
export function countDeliveringRows(
  section: DeliverySection,
  draft: NotificationPreferenceDraft,
  channelParentSwitches: readonly DraftParentSwitch[]
): number {
  return section.rows.filter((row) =>
    PREFERENCE_ROW_CHANNELS.some((channel) =>
      isChannelDelivering({
        row,
        channel,
        draft,
        channelParentSwitches,
        sectionParentSwitches: section.parentSwitches,
      })
    )
  ).length;
}

export type NotificationPreferenceDraftController = {
  /** Current toggle positions, which is what every switch on the page renders. */
  draft: NotificationPreferenceDraft;
  /** The server's answer, kept so "dirty" stays a comparison rather than a flag. */
  snapshot: NotificationPreferenceDraft;
  setToggle: (kind: string, channel: string, enabled: boolean) => void;
  /** The cadence the control is showing, moved or not. */
  cadence: NotificationDigestCadence;
  setCadence: (cadence: NotificationDigestCadence) => void;
  /**
   * True when the reader has moved the cadence. Separate from {@link dirty}
   * because that list is toggles and a cadence is not one — but it counts toward
   * the unsaved-changes total the footer shows.
   */
  isCadenceDirty: boolean;
  /** Throws the reader's edits away and goes back to the server's answer. */
  revert: () => void;
  dirty: DraftToggle[];
  /** Everything unsaved, toggles and cadence together. */
  dirtyCount: number;
  isDirty: boolean;
};

/**
 * Holds one tab's draft.
 *
 * Re-seeded whenever a new server answer arrives, which is what makes a save or
 * a reset settle: the mutation refetches, the catalog identity changes, and the
 * draft becomes the fresh answer with nothing left dirty. One of these exists per
 * tab, owned by the page rather than by the tab panel, so switching tabs is
 * never destructive.
 */
export function useNotificationPreferenceDraft(
  catalog: DraftCatalog | null | undefined
): NotificationPreferenceDraftController {
  const snapshot = useMemo(() => buildDraftFromCatalog(catalog), [catalog]);
  // Daily matches the platform default, so a page rendering before its query
  // lands shows the same cadence the server would have said rather than blank.
  const cadenceSnapshot = catalog?.emailDigest.cadence ?? NotificationDigestCadence.Daily;

  const [seededFrom, setSeededFrom] = useState<NotificationPreferenceDraft>(snapshot);
  const [draft, setDraft] = useState<NotificationPreferenceDraft>(snapshot);
  const [cadence, setCadenceState] = useState<NotificationDigestCadence>(cadenceSnapshot);

  // Re-seeded during render rather than in an effect. An effect would commit one
  // paint of the reader's stale edits against the new server answer first, which
  // on this page means a flash of switches in the positions a save just replaced.
  // The cadence rides the same signal so the two can never re-seed separately.
  if (seededFrom !== snapshot) {
    setSeededFrom(snapshot);
    setDraft(snapshot);
    setCadenceState(cadenceSnapshot);
  }

  const setToggle = useCallback((kind: string, channel: string, enabled: boolean) => {
    setDraft((current) => setDraftToggle(current, kind, channel, enabled));
  }, []);

  const revert = useCallback(() => {
    setDraft(snapshot);
    setCadenceState(cadenceSnapshot);
  }, [snapshot, cadenceSnapshot]);

  const dirty = useMemo(() => dirtyToggles(draft, snapshot), [draft, snapshot]);
  const isCadenceDirty = cadence !== cadenceSnapshot;

  return {
    draft,
    snapshot,
    setToggle,
    cadence,
    setCadence: setCadenceState,
    isCadenceDirty,
    revert,
    dirty,
    dirtyCount: dirty.length + (isCadenceDirty ? 1 : 0),
    isDirty: dirty.length > 0 || isCadenceDirty,
  };
}
